import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IUser } from './data/models/user/IUser';
import { 
  faRobot, 
  faComputer, 
  faUser,
  faKey,
  faCog,
  faTools,
  faQuestionCircle,
  faHouseLaptop,
  faAtom,
  faDesktop,
  faChartGantt,
  faChartArea,
  faChartPie,
  faMailBulk,
  faDesktopAlt,
  faMessage,
  faCalendar,
  faDollar,
  faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import { faAppStore, faDeskpro, faFacebookMessenger, faMailchimp, faMarkdown, faSignalMessenger } from '@fortawesome/free-brands-svg-icons';
import { MailIcon } from 'lucide-react';
import { faCalendarDays } from '@fortawesome/free-regular-svg-icons';

export interface Route {
  name: string;
  icon?: IconProp | string | string[];
  iconSet?: 'font-awesome' | 'feather' | 'unicons';
  pages?: Route[];
  path?: string;
  pathName?: string;
  flat?: boolean;
  topNavIcon?: string;
  dropdownInside?: boolean;
  active?: boolean;
  new?: boolean;
  hasNew?: boolean;
  next?: boolean;
  allowedGroups?: string[];
}

export interface RouteItems {
  label: string;
  horizontalNavLabel?: string;
  icon: IconProp; 
  labelDisabled?: boolean;
  pages: Route[];
  megaMenu?: boolean;
  active?: boolean;
}

export const filterRoutesByPermission = (
  routes: RouteItems[],
  user: IUser | null
): RouteItems[] => {
  if (!user) return [];

  const userGroups = user.groups.map(group => group.group.name);

  const filterRoutes = (pages: Route[]): Route[] => {
    return pages
      .filter(route => {
        if (!route.allowedGroups) return true;

        return route.allowedGroups.some(group => userGroups.includes(group));
      })
      .map(route => ({
        ...route,
        pages: route.pages ? filterRoutes(route.pages) : undefined,
      }));
  };

  return routes
    .map(routeItem => ({
      ...routeItem,
      pages: filterRoutes(routeItem.pages),
    }))
    .filter(routeItem => routeItem.pages.length > 0);
};

export const routes: RouteItems[] = [
  {
    label: 'MAIN MENU',
    horizontalNavLabel: 'home',
    active: true,
    icon: faRobot,
    labelDisabled: false,
    pages: [
      {
        name: `${process.env.REACT_APP_NAME} ${process.env.REACT_APP_TITLE}`,
        icon: faRobot,
        iconSet: 'font-awesome',
        active: true,
        flat: true,
        hasNew: true,
        allowedGroups: ['Chatbot Users'],
        path: '/anubis/AI'
      },
      {
        name: 'Dashboard AI',
        path: '/anubis/dashboard',
        pathName: 'dashboard',
        topNavIcon:'phone',
        icon: faChartPie,
        iconSet: 'font-awesome',
        allowedGroups: ['Default Group'],
        active: true,
      },
      {
        name: 'Workspace',
        icon: faHouseLaptop,
        iconSet: 'font-awesome', 
        active: true,
        flat: true,
        pathName: 'myaccount',
        allowedGroups: ['Shell Users', 'Workspace Users', 'Sales Users'],
        pages: [
          {
            name: 'Agenda AI',
            path: '/anubis/agenda',
            pathName: 'agenda',
            icon: faCalendarDays,
            iconSet: 'font-awesome',
            allowedGroups: ['Shell Users'],
            active: true
          },
          {
            name: 'E-Mail',
            path: '/anubis/email/inbox',
            pathName: 'email',
            icon: faMailBulk,
            iconSet: 'font-awesome', 
            allowedGroups: ['Default Group'],
            active: true
          },
          {
            name: 'Desktop',
            path: '/anubis/environment',
            pathName: 'environment',
            icon: faDesktop, 
            iconSet: 'font-awesome', 
            allowedGroups: ['Default Group'],
            active: true
          },
          {
            name: 'My Apps',
            path: '#/anubis/myapps',
            pathName: 'myapps',
            icon: faAppStore,
            iconSet: 'font-awesome',
            allowedGroups: ['Default Group'],
            active: false
          },
          {
            name: 'SalesHub',
            path: '/anubis/saleshub',
            pathName: 'SalesHub',
            icon: faDollarSign,
            iconSet: 'font-awesome',
            allowedGroups: ['Shell Users', 'Sales Users'],
            active: true
          },
          {
            name: 'Conversations',
            path: '/anubis/conversations',
            pathName: 'conversations',
            icon: faFacebookMessenger,
            iconSet: 'font-awesome',
            allowedGroups: ['Shell Users'],
            active: true
          }
        ]
      },
    ]
  },
  {
    label: 'ADMINISTRATION',
    horizontalNavLabel: 'administration',
    active: true,
    icon: faRobot,
    labelDisabled: false,
    pages: [
      {
        name: 'My Account',
        icon: 'user-check',
        iconSet: 'feather', 
        active: true,
        flat: true,
        pathName: 'myaccount',
        pages: [
          {
            name: 'Profile',
            path: 'profile/',
            pathName: 'myaccount',
            active: true,
            iconSet: 'feather'
          },
          {
            name: 'My Brain',
            path: 'mybrain/',
            pathName: 'mybrain',
            allowedGroups: ['Shell Users'],
            active: true,
            iconSet: 'feather'
          },
          {
            name: 'Security',
            path: '#4',
            pathName: 'myaccount',
            active: false,
            iconSet: 'feather',
            allowedGroups: ['Shell Users']
          },
          {
            name: 'Payment',
            path: '#3',
            pathName: 'myaccount',
            active: false,
            iconSet: 'feather',
            allowedGroups: ['Shell Users']
          },
          {
            name: 'API Center',
            path: '#2',
            pathName: 'myaccount',
            active: false,
            iconSet: 'feather',
            allowedGroups: ['Shell Users']
          },
          {
            name: 'My Cart',
            path: '#1',
            pathName: 'myaccount',
            active: false,
            iconSet: 'feather'
          }
        ]
      },
      {
        name: `${process.env.REACT_APP_NAME} Settings`,
        icon: faAtom, 
        iconSet: 'font-awesome', 
        active: true,
        flat: true,
        pathName: 'anubisAI',
        allowedGroups: ['Shell Users', 'Parent Users'],
        pages: [
          {
            name: 'Users & Tokens',
            icon: 'users',
            iconSet: 'feather', 
            active: true,
            flat: false,
            path: 'userstokens/',
            allowedGroups: ['Shell Users', 'Parent Users']
          },
          {
            name: `${process.env.REACT_APP_NAME} Neuroskills`,
            icon: 'cpu',
            iconSet: 'feather', 
            active: true,
            flat: false,
            path: 'neuroskills/',
            allowedGroups: ['Shell Users']
          },
          {
            name: `${process.env.REACT_APP_NAME} Prompts`,
            icon: 'command', 
            iconSet: 'feather',
            active: true,
            flat: false,
            path: 'prompts/',
            allowedGroups: ['Shell Users']
          }
        ]
      },
    ]
  },
  {
    label: 'CONFIGURATION',
    horizontalNavLabel: 'configuration',
    active: false,
    icon: faCog,
    labelDisabled: false,
    pages: [
      {
        name: 'General',
        icon: 'settings',
        iconSet: 'feather',
        active: false,
        flat: false,
        path: '#',
        allowedGroups: ['Default Group'],
      },
      {
        name: 'Advanced',
        icon: faTools,
        iconSet: 'font-awesome',
        active: true,
        flat: false,
        path: '#',
        allowedGroups: ['Default Group'],
          pages: [
            {
              name: 'Console',
              path: '/anubis/testshell',
              pathName: 'testshell',
              icon: 'terminal', 
              iconSet: 'feather', 
              allowedGroups: ['Default Group'],
              active: true
            },
            {
              name: 'AI Studio',
              path: '/anubis/anubis-studio',
              pathName: 'anubisstudio',
              icon: 'package', 
              iconSet: 'feather', 
              allowedGroups: ['Default Group'],
              active: true
            }
          ]
      }
    ]
  },
  {
    label: 'SUPPORT',
    horizontalNavLabel: 'support',
    active: true,
    icon: faQuestionCircle,
    labelDisabled: false,
    pages: [
      {
        name: 'Help Center',
        icon: 'help-circle',
        iconSet: 'feather', 
        active: false,
        flat: false,
        hasNew: true,
        dropdownInside: false,
        allowedGroups: ['Default Group'],
        pages: [
          {
            name: 'Documentation',
            icon: '', 
            iconSet: 'feather',
            active: false,
            flat: false,
            path: '#',
            allowedGroups: ['Shell Users']
          },
          {
            name: 'Request a ticket',
            path: '#',
            pathName: 'ticket',
            allowedGroups: ['Shell Users'],
            iconSet: 'feather'
          },
          {
            name: 'Contact Us',
            path: '#',
            new: true,
            pathName: 'contact',
            allowedGroups: ['Shell Users'],
            iconSet: 'feather'
          },
          {
            name: 'Terms & Privacy',
            path: '#',
            pathName: 'terms',
            allowedGroups: ['Shell Users'],
            iconSet: 'feather'
          },
          {
            name: 'Cookie Policy',
            path: '#',
            pathName: 'cookie',
            allowedGroups: ['Shell Users'],
            iconSet: 'feather'
          }
        ]
      }
    ]
  }
];